import type { CalendarEvent } from "./types.js";

export const PUBLIC_HOLIDAY_SOURCE_URL = "https://www.1823.gov.hk/tc/hong-kong-public-holidays-ical";
export const PUBLIC_HOLIDAY_VERIFIED_ON = "2026-10-11";
export const PUBLIC_HOLIDAY_YEARS = [2025, 2026, 2027] as const;

export function isCalendarHoliday(item: Pick<CalendarEvent, "type" | "schoolKind" | "lessonArrangement">) {
  return (
    item.type === "school" &&
    (item.schoolKind === "publicHoliday" || item.schoolKind === "schoolHoliday") &&
    (item.lessonArrangement === undefined || item.lessonArrangement === "noClasses")
  );
}

// Snapshot of https://www.1823.gov.hk/common/ical/tc.ics, checked on 2026-10-11.
// Use gazetted substitute dates; festivals on Sundays are not extra named holidays.
const holidays: ReadonlyArray<readonly [string, string]> = [
  ["2025-01-01", "一月一日"],
  ["2025-01-29", "農曆年初一"],
  ["2025-01-30", "農曆年初二"],
  ["2025-01-31", "農曆年初三"],
  ["2025-04-04", "清明節"],
  ["2025-04-18", "耶穌受難節"],
  ["2025-04-19", "耶穌受難節翌日"],
  ["2025-04-21", "復活節星期一"],
  ["2025-05-01", "勞動節"],
  ["2025-05-05", "佛誕"],
  ["2025-05-31", "端午節"],
  ["2025-07-01", "香港特別行政區成立紀念日"],
  ["2025-10-01", "國慶日"],
  ["2025-10-07", "中秋節翌日"],
  ["2025-10-29", "重陽節"],
  ["2025-12-25", "聖誕節"],
  ["2025-12-26", "聖誕節後第一個周日"],
  ["2026-01-01", "一月一日"],
  ["2026-02-17", "農曆年初一"],
  ["2026-02-18", "農曆年初二"],
  ["2026-02-19", "農曆年初三"],
  ["2026-04-03", "耶穌受難節"],
  ["2026-04-04", "耶穌受難節翌日"],
  ["2026-04-06", "清明節翌日"],
  ["2026-04-07", "復活節星期一翌日"],
  ["2026-05-01", "勞動節"],
  ["2026-05-25", "佛誕翌日"],
  ["2026-06-19", "端午節"],
  ["2026-07-01", "香港特別行政區成立紀念日"],
  ["2026-09-26", "中秋節翌日"],
  ["2026-10-01", "國慶日"],
  ["2026-10-19", "重陽節翌日"],
  ["2026-12-25", "聖誕節"],
  ["2026-12-26", "聖誕節後第一個周日"],
  ["2027-01-01", "一月一日"],
  ["2027-02-06", "農曆年初一"],
  ["2027-02-08", "農曆年初三"],
  ["2027-02-09", "農曆年初四"],
  ["2027-03-26", "耶穌受難節"],
  ["2027-03-27", "耶穌受難節翌日"],
  ["2027-03-29", "復活節星期一"],
  ["2027-04-05", "清明節"],
  ["2027-05-01", "勞動節"],
  ["2027-05-13", "佛誕"],
  ["2027-06-09", "端午節"],
  ["2027-07-01", "香港特別行政區成立紀念日"],
  ["2027-09-16", "中秋節翌日"],
  ["2027-10-01", "國慶日"],
  ["2027-10-08", "重陽節"],
  ["2027-12-25", "聖誕節"],
  ["2027-12-27", "聖誕節後第一個周日"],
];

const aliases: Record<string, readonly string[]> = {
  一月一日: ["元旦", "元旦日", "New Year's Day", "The first day of January"],
  農曆年初一: ["農曆新年初一", "Lunar New Year's Day"],
  農曆年初二: ["農曆新年初二", "The second day of Lunar New Year"],
  農曆年初三: ["農曆新年初三", "The third day of Lunar New Year"],
  農曆年初四: ["農曆新年初四", "The fourth day of Lunar New Year"],
  清明節: ["Ching Ming Festival"],
  清明節翌日: ["The day following Ching Ming Festival"],
  耶穌受難節: ["Good Friday"],
  耶穌受難節翌日: ["The day following Good Friday"],
  復活節星期一: ["Easter Monday"],
  復活節星期一翌日: ["The day following Easter Monday"],
  勞動節: ["Labour Day"],
  佛誕: ["The Birthday of the Buddha"],
  佛誕翌日: ["The day following the Birthday of the Buddha"],
  端午節: ["Tuen Ng Festival"],
  香港特別行政區成立紀念日: [
    "香港特區成立紀念日",
    "特區成立紀念日",
    "Hong Kong Special Administrative Region Establishment Day",
  ],
  中秋節翌日: ["中秋翌日", "The day following the Chinese Mid-Autumn Festival"],
  國慶日: ["國慶", "National Day"],
  重陽節: ["Chung Yeung Festival"],
  重陽節翌日: ["The day following Chung Yeung Festival"],
  聖誕節: ["Christmas Day"],
  聖誕節後第一個周日: ["聖誕節翌日", "The first weekday after Christmas Day"],
};

function normaliseTitle(title: string) {
  return title
    .replace(/[（(](?:公眾假期|public holiday)[）)]/gi, "")
    .replace(/[\s'’]/g, "")
    .toLowerCase();
}
const canonicalNames = new Map(
  Object.entries(aliases).flatMap(([name, alternatives]) =>
    [name, ...alternatives].map((text) => [normaliseTitle(text), name] as const),
  ),
);

export function isPublicHolidayTitle(title: string) {
  return canonicalNames.has(normaliseTitle(title)) || /^(公眾假期|publicholiday)$/.test(normaliseTitle(title));
}

export function isHongKongPublicHolidayDate(date: string) {
  const value = new Date(`${date}T00:00:00Z`);
  return (
    !Number.isNaN(value.getTime()) &&
    value.toISOString().slice(0, 10) === date &&
    (value.getUTCDay() === 0 || holidays.some(([day]) => day === date))
  );
}

export function matchingPublicHoliday(item: { startDate?: string; endDate?: string; title: string }) {
  if (!item.startDate || (item.endDate && item.endDate !== item.startDate)) return undefined;
  const name = canonicalNames.get(normaliseTitle(item.title));
  const found = holidays.find(([date, title]) => date === item.startDate && title === name);
  return found ? { date: found[0], title: found[1] } : undefined;
}

export function hongKongPublicHolidays(workspaceId: string): CalendarEvent[] {
  return holidays.map(([date, title]) => ({
    id: `hk-public-holiday:${date}`,
    workspaceId,
    date,
    title,
    type: "school",
    schoolKind: "publicHoliday",
    allDay: true,
    timezone: "Asia/Hong_Kong",
    createdAt: `${PUBLIC_HOLIDAY_VERIFIED_ON}T00:00:00Z`,
    updatedAt: `${PUBLIC_HOLIDAY_VERIFIED_ON}T00:00:00Z`,
    source: { kind: "official", sourceName: "香港政府 1823", sourceRef: PUBLIC_HOLIDAY_SOURCE_URL },
  }));
}

// Derive this only for display. Stored imports and backup contents stay intact.
export function calendarWithPublicHolidays(events: CalendarEvent[], workspaceId: string): CalendarEvent[] {
  return [
    ...events.filter(
      (item) =>
        !(
          item.source?.kind === "official" ||
          (!item.classId &&
            item.type === "school" &&
            item.schoolKind === "publicHoliday" &&
            matchingPublicHoliday({ startDate: item.date, endDate: item.endDate, title: item.title }))
        ),
    ),
    ...hongKongPublicHolidays(workspaceId),
  ];
}
